import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { RESOURCE_TYPES, PROJECT_STATUSES, STATUS_COLORS, PROJECT_TYPES, CATEGORIES } from '@/lib/constants';
import { calculateWeeks, getWeekLabels, getProjectDuration, formatCurrency } from '@/lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Save, FileText, CalendarIcon, Plus, Trash2, ArrowLeft, Clock, DollarSign, Download, Copy, Lock, Unlock, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import * as XLSX from 'xlsx';
import { Upload, X, File } from 'lucide-react';

const DEFAULT_RESOURCES = ['data_engineer', 'data_architect', 'project_manager', 'qa_testing'];

const createEmptyResource = (type) => {
  const resType = RESOURCE_TYPES.find(r => r.value === type) || RESOURCE_TYPES[0];
  return {
    type: resType.value,
    label: resType.label,
    rate: resType.defaultRate,
    weeklyAllocation: [],
  };
};

export default function EstimateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const cameFromHeatmap = location.state?.from === 'heatmap';

  const [project, setProject] = useState({
    id: '',
    clientName: '',
    projectName: '',
    startDate: '',
    endDate: '',
    projectLead: '',
    status: 'draft',
    projectType: 'staff_aug',
    category: 'regular',
    poNumber: '',
    projectTeam: [],  // Array of email addresses
    resources: DEFAULT_RESOURCES.map(r => createEmptyResource(r)),
    documents: {  // Documents organized by phase
      proposal: [],    // For 'draft' and 'proposal' phases
      sow: [],         // For 'sow' phase
      execute: [],     // For 'execute' phase
    },
  });

  const [applyAllWeeks, setApplyAllWeeks] = useState({});
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [passkeyInput, setPasskeyInput] = useState('');
  const [passkeyAction, setPasskeyAction] = useState('unlock'); // 'lock' or 'unlock'
  const [passkeyError, setPasskeyError] = useState('');
  const [userUnlocked, setUserUnlocked] = useState(false);
  const [activeVersion, setActiveVersion] = useState(1);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [savingLoading, setSavingLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(!isNew);

  const isLockable = ['sow', 'execute'].includes(project.status);
  const isLocked = isLockable && !isNew && !userUnlocked && project.isLocked !== false;
  const isVersionable = ['draft', 'proposal'].includes(project.status);

  useEffect(() => {
    const loadData = async () => {
      if (!isNew) {
        setIsLoading(true);
        try {
          const existing = await api.getProject(id);
          if (existing) {
            // Auto-initialize V1 for Draft/Proposal projects that have no versions yet
            if (['draft', 'proposal'].includes(existing.status) && (!existing.versions || existing.versions.length === 0)) {
              existing.versions = [{ version: 1, resources: (existing.resources || []).map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })) }];
            }
            // Initialize documents if not present
            if (!existing.documents) {
              existing.documents = { proposal: [], sow: [], execute: [] };
            }
            setProject(existing);
            if (existing.versions && existing.versions.length > 0) {
              setActiveVersion(existing.selectedVersion || existing.versions[0].version || 1);
            }
          } else {
            navigate('/');
          }
        } catch {
          navigate('/');
        } finally {
          setIsLoading(false);
        }
      } else {
        try {
          const nextId = await api.getNextId();
          setProject(prev => ({ ...prev, id: nextId }));
        } catch {
          setProject(prev => ({ ...prev, id: `EST-${Date.now()}` }));
        }
      }
    };
    loadData();
  }, [id, isNew, navigate]);

  const numWeeks = calculateWeeks(project.startDate, project.endDate);
  const weekLabels = getWeekLabels(project.startDate, numWeeks);
  const duration = getProjectDuration(project.startDate, project.endDate);

  // Ensure all resources have correct allocation array length
  useEffect(() => {
    if (numWeeks > 0) {
      setProject(prev => ({
        ...prev,
        resources: prev.resources.map(res => {
          const current = res.weeklyAllocation || [];
          if (current.length !== numWeeks) {
            const newAlloc = Array(numWeeks).fill(0);
            current.forEach((val, i) => {
              if (i < numWeeks) newAlloc[i] = val;
            });
            return { ...res, weeklyAllocation: newAlloc };
          }
          return res;
        }),
      }));
    }
  }, [numWeeks]);

  const updateField = (field, value) => {
    // Intercept status change to SoW/Execute from Draft/Proposal when multiple versions exist
    if (field === 'status' && ['sow', 'execute'].includes(value) && ['draft', 'proposal'].includes(project.status)) {
      const vers = project.versions || [];
      if (vers.length > 1) {
        setPendingStatus(value);
        setShowVersionModal(true);
        return;
      }
    }
    setProject(prev => ({ ...prev, [field]: value }));
  };

  const updateResource = (idx, field, value) => {
    setProject(prev => {
      const resources = [...prev.resources];
      resources[idx] = { ...resources[idx], [field]: value };
      return { ...prev, resources };
    });
  };

  const updateAllocation = (resIdx, weekIdx, value) => {
    setProject(prev => {
      const resources = [...prev.resources];
      const alloc = [...(resources[resIdx].weeklyAllocation || [])];
      const numVal = Number(value) || 0;
      alloc[weekIdx] = numVal;
      // If "apply to all" is checked and week 1 (index 0) changed, propagate
      if (applyAllWeeks[resIdx] && weekIdx === 0) {
        for (let i = 1; i < alloc.length; i++) alloc[i] = numVal;
      }
      resources[resIdx] = { ...resources[resIdx], weeklyAllocation: alloc };
      return { ...prev, resources };
    });
  };

  const toggleApplyAll = (resIdx, checked) => {
    setApplyAllWeeks(prev => ({ ...prev, [resIdx]: checked }));
    if (checked) {
      setProject(prev => {
        const resources = [...prev.resources];
        const alloc = [...(resources[resIdx].weeklyAllocation || [])];
        const week1Val = alloc[0] || 0;
        for (let i = 1; i < alloc.length; i++) alloc[i] = week1Val;
        resources[resIdx] = { ...resources[resIdx], weeklyAllocation: alloc };
        return { ...prev, resources };
      });
    }
  };

  const changeResourceType = (resIdx, newType) => {
    const resType = RESOURCE_TYPES.find(r => r.value === newType);
    if (!resType) return;
    setProject(prev => {
      const resources = [...prev.resources];
      resources[resIdx] = {
        ...resources[resIdx],
        type: resType.value,
        label: resType.label,
        rate: resType.defaultRate,
      };
      return { ...prev, resources };
    });
  };

  const addResource = () => {
    const usedTypes = project.resources.map(r => r.type);
    const available = RESOURCE_TYPES.find(r => !usedTypes.includes(r.value));
    const newRes = createEmptyResource(available ? available.value : RESOURCE_TYPES[0].value);
    newRes.weeklyAllocation = Array(numWeeks).fill(0);
    setProject(prev => ({ ...prev, resources: [...prev.resources, newRes] }));
  };

  const removeResource = (idx) => {
    if (project.resources.length <= 1) return;
    setProject(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== idx),
    }));
  };

  // Project Team management
  const [teamEmailInput, setTeamEmailInput] = useState('');
  const [teamEmailError, setTeamEmailError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [uploadingPhase, setUploadingPhase] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});

  // Get current phase's document type based on status
  const getDocumentPhase = () => {
    if (['draft', 'proposal'].includes(project.status)) return 'proposal';
    if (project.status === 'sow') return 'sow';
    if (project.status === 'execute') return 'execute';
    return null;
  };

  const currentPhase = getDocumentPhase();

  // Handle document upload to backend
  const handleDocumentUpload = async (e, phase) => {
    const files = Array.from(e.target.files || []);
    
    for (const file of files) {
      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        // Upload to backend
        const document = await api.uploadDocument(file, project.id, phase);
        
        // Add to project documents
        setProject(prev => ({
          ...prev,
          documents: {
            ...prev.documents,
            [phase]: [...(prev.documents?.[phase] || []), document],
          },
        }));
        
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error('Error uploading document:', error);
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[file.name];
          return updated;
        });
      }
    }
  };

  // Remove document from backend
  const removeDocument = async (phase, docId) => {
    try {
      // docId is actually the blob_name in this case
      await api.deleteDocument(docId, project.id);
      
      setProject(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [phase]: (prev.documents?.[phase] || []).filter(doc => doc.id !== docId),
        },
      }));
      
      toast.success('Document removed successfully');
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error('Failed to remove document');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await api.listUsers();
        setAllUsers(users || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Filter users based on input
  const filteredUsers = teamEmailInput.trim()
    ? allUsers.filter(user => {
        const searchTerm = teamEmailInput.toLowerCase();
        const alreadyAdded = (project.projectTeam || []).includes(user.email);
        return !alreadyAdded && (
          user.email.toLowerCase().includes(searchTerm) ||
          (user.full_name && user.full_name.toLowerCase().includes(searchTerm)) ||
          (user.username && user.username.toLowerCase().includes(searchTerm))
        );
      })
    : [];

  const addTeamMember = (email = null) => {
    const emailToAdd = email || teamEmailInput.trim();
    if (!emailToAdd) {
      setTeamEmailError('Please enter an email address');
      return;
    }
    if (!validateEmail(emailToAdd)) {
      setTeamEmailError('Please enter a valid email address');
      return;
    }
    if ((project.projectTeam || []).includes(emailToAdd)) {
      setTeamEmailError('This email is already added');
      return;
    }
    setProject(prev => ({
      ...prev,
      projectTeam: [...(prev.projectTeam || []), emailToAdd],
    }));
    setTeamEmailInput('');
    setTeamEmailError('');
    setShowUserDropdown(false);
  };

  const removeTeamMember = (email) => {
    setProject(prev => ({
      ...prev,
      projectTeam: (prev.projectTeam || []).filter(e => e !== email),
    }));
  };

  // --- Version management ---
  const saveCurrentToVersions = (proj) => {
    const vers = [...(proj.versions || [])];
    const idx = vers.findIndex(v => v.version === activeVersion);
    const snapshot = { version: activeVersion, resources: proj.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })) };
    if (idx >= 0) vers[idx] = snapshot;
    else vers.push(snapshot);
    return vers;
  };

  const createNewVersion = () => {
    const vers = saveCurrentToVersions(project);
    const nextNum = vers.length + 1;
    if (nextNum > 3) return;
    const newResources = project.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] }));
    vers.push({ version: nextNum, resources: newResources });
    setProject(prev => ({ ...prev, versions: vers, resources: newResources }));
    setActiveVersion(nextNum);
    toast.success(`Version V${nextNum} created`);
  };

  const switchVersion = (vNum) => {
    if (vNum === activeVersion) return;
    const vers = saveCurrentToVersions(project);
    const target = vers.find(v => v.version === vNum);
    if (target) {
      setProject(prev => ({ ...prev, versions: vers, resources: target.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })) }));
      setActiveVersion(vNum);
    }
  };

  const finalizeVersion = (vNum) => {
    const vers = saveCurrentToVersions(project);
    const target = vers.find(v => v.version === vNum);
    if (target) {
      setProject(prev => ({
        ...prev,
        status: pendingStatus,
        resources: target.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })),
        versions: vers,
        selectedVersion: vNum,
      }));
    }
    setShowVersionModal(false);
    setPendingStatus(null);
    toast.success(`Version V${vNum} selected as final estimate`);
  };

  const getResourceTotal = (res) => {
    return (res.weeklyAllocation || []).reduce((s, v) => s + (v || 0), 0) * (res.rate || 0);
  };

  const getWeekTotal = (weekIdx) => {
    return project.resources.reduce((sum, res) => {
      return sum + ((res.weeklyAllocation || [])[weekIdx] || 0) * (res.rate || 0);
    }, 0);
  };

  const grandTotal = project.resources.reduce((sum, res) => sum + getResourceTotal(res), 0);

  const openPasskeyModal = (action) => {
    setPasskeyAction(action);
    setPasskeyInput('');
    setPasskeyError('');
    setShowPasskeyModal(true);
  };

  const handlePasskeySubmit = async () => {
    try {
      if (passkeyAction === 'lock') {
        await api.lockProject(project.id, passkeyInput);
        setProject(prev => ({ ...prev, isLocked: true }));
        setUserUnlocked(false);
        toast.success('Estimates locked successfully');
      } else {
        await api.unlockProject(project.id, passkeyInput);
        setProject(prev => ({ ...prev, isLocked: false }));
        setUserUnlocked(true);
        toast.success('Estimates unlocked for editing');
      }
      setShowPasskeyModal(false);
    } catch {
      setPasskeyError('Invalid passkey. Please try again.');
    }
  };

  const handleExportExcel = () => {
    if (numWeeks === 0) return;
    const data = project.resources.map(res => {
      const row = { 'Resource': res.label, 'Rate ($/wk)': res.rate };
      weekLabels.forEach((label, i) => { row[label] = (res.weeklyAllocation || [])[i] || 0; });
      row['Total ($)'] = getResourceTotal(res);
      return row;
    });
    // Add weekly totals row
    const totalsRow = { 'Resource': 'Weekly Total', 'Rate ($/wk)': '' };
    weekLabels.forEach((label, i) => { totalsRow[label] = getWeekTotal(i); });
    totalsRow['Total ($)'] = grandTotal;
    data.push(totalsRow);

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Estimate');
    XLSX.writeFile(wb, `${project.id}_estimate.xlsx`);
    toast.success('Estimate table exported to Excel');
  };

  const handleSave = async () => {
    if (!project.projectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (project.status === 'execute' && !(project.poNumber || '').trim()) {
      toast.error('PO Number is required for Execute status. Assign a PO or use the dummy PO option.');
      return;
    }
    const versionsToSave = saveCurrentToVersions(project);
    const dataToSave = { ...project, versions: versionsToSave, selectedVersion: project.selectedVersion || activeVersion };
    try {
      setSavingLoading(true);
      if (isNew) {
        const saved = await api.createProject(dataToSave);
        setProject(saved);
      } else {
        const saved = await api.updateProject(project.id, dataToSave);
        setProject(saved);
      }
      toast.success('Estimate saved successfully');
    } catch {
      toast.error('Failed to save estimate');
    } finally {
      setSavingLoading(false);
    }
  };

  const handleSaveAndViewInvoices = async () => {
    if (!project.projectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (!project.startDate || !project.endDate) {
      toast.error('Start and end dates are required for invoices');
      return;
    }
    if (project.status === 'draft' || project.status === 'proposal') {
      toast.error('Invoices cannot be created for projects in Draft or Proposal phase. Move the project to SoW or Execute status first.');
      return;
    }
    if (project.status === 'execute' && !(project.poNumber || '').trim()) {
      toast.error('PO Number is required for Execute status. Assign a PO or use the dummy PO option.');
      return;
    }
    const versionsToSave = saveCurrentToVersions(project);
    const dataToSave = { ...project, versions: versionsToSave, selectedVersion: project.selectedVersion || activeVersion };
    try {
      setSavingLoading(true);
      if (isNew) {
        await api.createProject(dataToSave);
      } else {
        await api.updateProject(project.id, dataToSave);
      }
      toast.success('Estimate saved');
      navigate(`/invoices/${project.id}`);
    } catch {
      toast.error('Failed to save estimate');
    } finally {
      setSavingLoading(false);
    }
  };

  return (
    <div data-testid="estimate-form-page">
      <Toaster theme="dark" />

      {isLoading ? (
        // Skeleton Loading State
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-48" />
            </div>
          </div>

          {/* Lock Banner Skeleton */}
          <Skeleton className="h-12 w-full rounded-lg" />

          {/* Project Details Card Skeleton */}
          <Card className="bg-zinc-950 border-zinc-900">
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Resources Table Skeleton */}
          <Card className="bg-zinc-950 border-zinc-900">
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Card Skeleton */}
          <Card className="bg-zinc-950 border-zinc-900">
            <CardContent className="p-6">
              <Skeleton className="h-6 w-32 mb-4" />
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        // Main Form Content
        <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => cameFromHeatmap ? navigate('/', { state: { tab: 'resource-heatmap' } }) : navigate('/')}
            data-testid="back-to-dashboard-btn"
            className="text-zinc-400 hover:text-white hover:bg-zinc-900 flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          {cameFromHeatmap && (
            <Badge className="bg-zinc-900 text-zinc-400 border-zinc-800 border text-[10px] rounded-full font-manrope cursor-pointer hover:text-brand-400 hover:border-brand-500/30 transition-colors flex-shrink-0"
              onClick={() => navigate('/', { state: { tab: 'resource-heatmap' } })}
              data-testid="back-to-heatmap-badge"
            >
              Back to Heatmap
            </Badge>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-manrope font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl tracking-tight text-white" data-testid="estimate-title">
                {isNew ? 'New Estimate' : 'Edit Estimate'}
              </h1>
              <Badge className="bg-brand-500/10 text-brand-400 border-brand-500/20 border font-mono text-[9px] sm:text-xs rounded-full flex-shrink-0" data-testid="project-id-badge">
                {project.id}
              </Badge>
            </div>
            {duration && (
              <p className="text-zinc-500 text-xs sm:text-sm mt-0.5 flex items-center gap-1.5">
                <Clock className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                Duration: {duration}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
          <Button
            data-testid="save-project-btn"
            onClick={handleSave}
            disabled={savingLoading}
            className="bg-zinc-900 text-zinc-100 border border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700 font-medium disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {savingLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button
            data-testid="save-and-invoices-btn"
            onClick={handleSaveAndViewInvoices}
            disabled={savingLoading}
            className="bg-brand-500 text-white hover:bg-brand-400 hover:shadow-[0_0_20px_-5px_rgba(168,0,151,0.5)] font-semibold active:scale-95 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {savingLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Save & View Invoices
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Lock Banner for SoW/Execute */}
      {isLockable && (
        <div className={`mb-4 flex items-center justify-between p-3 rounded-lg border ${
          isLocked
            ? 'bg-amber-500/5 border-amber-500/20'
            : 'bg-zinc-900/50 border-zinc-800'
        }`} data-testid="lock-banner">
          <div className="flex items-center gap-2">
            {isLocked ? (
              <>
                <Lock className="w-4 h-4 text-amber-400" />
                <span className="font-manrope text-sm text-amber-300">Resource estimates are locked. Enter passkey to unlock for editing.</span>
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 text-zinc-400" />
                <span className="font-manrope text-sm text-zinc-400">Resource estimates are unlocked. Lock to prevent accidental changes.</span>
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openPasskeyModal(isLocked ? 'unlock' : 'lock')}
            data-testid="lock-toggle-btn"
            className={isLocked
              ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-xs'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800 text-xs'
            }
          >
            {isLocked ? <><Unlock className="w-3.5 h-3.5 mr-1.5" />Unlock</> : <><Lock className="w-3.5 h-3.5 mr-1.5" />Lock Estimates</>}
          </Button>
        </div>
      )}

      {/* Passkey Modal */}
      <Dialog open={showPasskeyModal} onOpenChange={setShowPasskeyModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm" data-testid="passkey-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">
              {passkeyAction === 'lock' ? 'Lock Estimates' : 'Unlock Estimates'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-zinc-400 text-sm font-manrope">
              {passkeyAction === 'lock'
                ? 'Enter passkey to lock the estimates and prevent further edits.'
                : 'Enter passkey to unlock the estimates for editing.'}
            </p>
            <Input
              type="password"
              value={passkeyInput}
              onChange={e => { setPasskeyInput(e.target.value); setPasskeyError(''); }}
              onKeyDown={e => e.key === 'Enter' && handlePasskeySubmit()}
              placeholder="Enter passkey"
              data-testid="passkey-input"
              className="bg-zinc-950 border-zinc-800 focus:border-brand-500 font-mono text-sm"
              autoFocus
            />
            {passkeyError && <p className="text-red-400 text-xs font-manrope" data-testid="passkey-error">{passkeyError}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPasskeyModal(false)} className="text-zinc-400 hover:text-white">Cancel</Button>
            <Button
              onClick={handlePasskeySubmit}
              data-testid="passkey-submit-btn"
              className="bg-brand-500 text-white hover:bg-brand-400 font-semibold"
            >
              {passkeyAction === 'lock' ? 'Lock' : 'Unlock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Details */}
      <Card className="bg-zinc-950 border-zinc-900 mb-6" data-testid="project-details-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-manrope font-bold text-base text-white">Project Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Client Name</Label>
              <Input
                data-testid="client-name-input"
                value={project.clientName}
                onChange={e => updateField('clientName', e.target.value)}
                placeholder="Enter client name"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Name</Label>
              <Input
                data-testid="project-name-input"
                value={project.projectName}
                onChange={e => updateField('projectName', e.target.value)}
                placeholder="Enter project name"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Lead</Label>
              <Input
                data-testid="project-lead-input"
                value={project.projectLead}
                onChange={e => updateField('projectLead', e.target.value)}
                placeholder="Enter project lead"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Start Date</Label>
              <div className="relative">
                <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <Input
                  type="date"
                  data-testid="start-date-btn"
                  value={project.startDate || ''}
                  onChange={e => updateField('startDate', e.target.value)}
                  className={`relative w-full pl-9 font-mono text-sm bg-zinc-950 border-zinc-800 hover:border-zinc-700 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${
                    !project.startDate ? 'text-zinc-600' : 'text-zinc-200'
                  }`}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">End Date</Label>
              <div className="relative">
                <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <Input
                  type="date"
                  data-testid="end-date-btn"
                  value={project.endDate || ''}
                  min={project.startDate || undefined}
                  onChange={e => updateField('endDate', e.target.value)}
                  className={`relative w-full pl-9 font-mono text-sm bg-zinc-950 border-zinc-800 hover:border-zinc-700 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${
                    !project.endDate ? 'text-zinc-600' : 'text-zinc-200'
                  }`}
                />
              </div>
              {project.startDate && !project.endDate && (
                <p className="text-xs text-amber-400 mt-1.5 font-manrope">End date is required to configure resources</p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Status</Label>
              <Select value={project.status} onValueChange={(val) => updateField('status', val)}>
                <SelectTrigger data-testid="project-status-select" className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {PROJECT_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value} className="text-zinc-200 focus:bg-zinc-900 focus:text-white">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Type</Label>
              <Select value={project.projectType || 'staff_aug'} onValueChange={(val) => updateField('projectType', val)}>
                <SelectTrigger data-testid="project-type-select" className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {PROJECT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-zinc-200 focus:bg-zinc-900 focus:text-white">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Category</Label>
              <Select value={project.category || 'regular'} onValueChange={(val) => updateField('category', val)}>
                <SelectTrigger data-testid="category-select" className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value} className="text-zinc-200 focus:bg-zinc-900 focus:text-white">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">PO Number</Label>
              <Input
                data-testid="po-number-input"
                value={project.poNumber || ''}
                onChange={e => updateField('poNumber', e.target.value)}
                placeholder="Optional"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
              {project.status === 'execute' && !(project.poNumber || '').trim() && (
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    data-testid="dummy-po-checkbox"
                    checked={project.poNumber === '999999'}
                    onCheckedChange={(checked) => updateField('poNumber', checked ? '999999' : '')}
                    className="border-zinc-600 data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500"
                  />
                  <span className="text-xs text-amber-400 font-manrope">Assign dummy PO (999999)</span>
                </div>
              )}
            </div>

            {/* Project Team */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Team</Label>
              <div className="space-y-2">
                <div className="relative flex gap-2">
                  <Input
                    value={teamEmailInput}
                    onChange={e => {
                      setTeamEmailInput(e.target.value);
                      setTeamEmailError('');
                      setShowUserDropdown(true);
                    }}
                    onKeyPress={e => e.key === 'Enter' && addTeamMember()}
                    onFocus={() => teamEmailInput && setShowUserDropdown(true)}
                    onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                    placeholder="Search by email, name, or username"
                    className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600 flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => addTeamMember()}
                    size="sm"
                    className="bg-brand-500 hover:bg-brand-400 text-white font-manrope text-xs px-3 h-9"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  
                  {/* User Dropdown */}
                  {showUserDropdown && filteredUsers.length > 0 && (
                    <div className="absolute top-full left-0 right-12 mt-1 bg-zinc-900 border border-zinc-800 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                      {filteredUsers.map((user) => (
                        <button
                          key={user.email}
                          type="button"
                          onClick={() => addTeamMember(user.email)}
                          className="w-full text-left px-3 py-2 hover:bg-zinc-800 border-b border-zinc-800/50 last:border-b-0 transition-colors"
                        >
                          <div className="flex flex-col">
                            <span className="font-manrope text-sm text-white">
                              {user.full_name || user.username}
                            </span>
                            <span className="font-mono text-xs text-zinc-400">{user.email}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {teamEmailError && (
                  <p className="text-xs text-red-400 font-manrope">{teamEmailError}</p>
                )}
                {(project.projectTeam || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {project.projectTeam.map(email => {
                      const user = allUsers.find(u => u.email === email);
                      const displayName = user?.full_name || user?.username || email;
                      return (
                        <Badge
                          key={email}
                          variant="secondary"
                          className="bg-zinc-900 border-zinc-800 text-zinc-300 text-xs font-manrope py-1 px-2 flex items-center gap-2 cursor-pointer hover:bg-zinc-800"
                        >
                          <span>{displayName}</span>
                          <button
                            type="button"
                            onClick={() => removeTeamMember(email)}
                            className="ml-1 text-red-400 hover:text-red-300"
                          >
                            ×
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {duration && (
            <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-500" />
                <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Duration</span>
                <span className="font-mono text-sm text-white" data-testid="project-duration">{duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-brand-500" />
                <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Total Estimate</span>
                <span className="font-mono text-sm text-brand-400" data-testid="grand-total">{formatCurrency(grandTotal)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents Section */}
      {currentPhase && (
        <Card className="bg-zinc-950 border-zinc-900 mb-6" data-testid="documents-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-manrope font-bold text-base text-white flex items-center gap-2">
              <File className="w-5 h-5 text-brand-500" />
              {currentPhase === 'proposal' && 'Proposal Documents'}
              {currentPhase === 'sow' && 'SoW Documents (Shared with Customer)'}
              {currentPhase === 'execute' && 'Execution Documents (PO, Deliverables, Proof of Done, Demo, Marketing Deck, etc.)'}
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1 font-manrope">
              {currentPhase === 'proposal' && 'Upload proposal documents for this phase'}
              {currentPhase === 'sow' && 'Upload Statement of Work shared with the customer'}
              {currentPhase === 'execute' && 'Upload PO, deliverables, proof of done, demos, marketing materials, and other execution documents'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Upload Area */}
            <div className="relative border-2 border-dashed border-zinc-800 rounded-lg p-6 hover:border-brand-500/50 transition-colors cursor-pointer group">
              <input
                type="file"
                multiple
                onChange={(e) => handleDocumentUpload(e, currentPhase)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={Object.keys(uploadProgress).length > 0}
                data-testid={`document-upload-input-${currentPhase}`}
              />
              <div className="flex flex-col items-center justify-center text-center pointer-events-none">
                <Upload className="w-8 h-8 text-zinc-600 group-hover:text-brand-500 transition-colors mb-2" />
                <p className="text-sm font-manrope text-zinc-400 group-hover:text-zinc-300">
                  {Object.keys(uploadProgress).length > 0 ? 'Uploading...' : 'Drag and drop files here or click to browse'}
                </p>
                <p className="text-xs text-zinc-500 mt-1">Supported: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG</p>
              </div>
            </div>

            {/* Upload Progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <div className="space-y-2 p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg">
                {Object.entries(uploadProgress).map(([fileName]) => (
                  <div key={fileName} className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-brand-500 animate-spin" />
                    <span className="text-xs font-manrope text-zinc-400">{fileName}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Uploaded Documents */}
            {(project.documents?.[currentPhase] || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-3">Uploaded Files ({(project.documents?.[currentPhase] || []).length})</p>
                <div className="space-y-2">
                  {(project.documents?.[currentPhase] || []).map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg hover:bg-zinc-900/70 hover:border-brand-500/30 transition-colors group"
                      data-testid={`document-item-${doc.id}`}
                    >
                      <File className="w-4 h-4 text-brand-500 flex-shrink-0 group-hover:text-brand-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-manrope text-white truncate group-hover:text-brand-400">{doc.name}</p>
                        <p className="text-xs text-zinc-500">
                          {formatFileSize(doc.size)} • {new Date(doc.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <a
                        href={doc.url}
                        download={doc.name}
                        className="text-zinc-400 hover:text-brand-400 hover:bg-brand-500/10 rounded-lg p-2 transition-colors flex-shrink-0"
                        title="Download document"
                        data-testid={`download-document-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          removeDocument(currentPhase, doc.id);
                        }}
                        className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg p-2 transition-colors flex-shrink-0"
                        data-testid={`remove-document-${doc.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(project.documents?.[currentPhase] || []).length === 0 && (
              <div className="text-center py-4">
                <p className="text-xs text-zinc-500 font-manrope">No documents uploaded yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Version Selector for Draft/Proposal */}
      {isVersionable && !isNew && (
        <div className="mb-4 flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800" data-testid="version-selector">
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Estimate Version:</span>
          <div className="flex items-center gap-1">
            {(project.versions || []).map(v => (
              <Button
                key={v.version}
                size="sm"
                variant={activeVersion === v.version ? 'default' : 'ghost'}
                onClick={() => switchVersion(v.version)}
                data-testid={`version-btn-${v.version}`}
                className={activeVersion === v.version
                  ? 'bg-brand-500 text-white hover:bg-brand-400 text-xs h-7 px-3 font-mono'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800 text-xs h-7 px-3 font-mono'
                }
              >
                V{v.version}
              </Button>
            ))}
            {(project.versions || []).length < 3 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={createNewVersion}
                data-testid="create-version-btn"
                className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 text-xs h-7 px-2"
              >
                <Plus className="w-3 h-3 mr-1" />
                V{(project.versions || []).length + 1}
              </Button>
            )}
          </div>
          <span className="text-[10px] text-zinc-600 font-manrope ml-auto">Up to 3 versions allowed</span>
        </div>
      )}

      {/* Version Selection Modal (when moving to SoW/Execute) */}
      <Dialog open={showVersionModal} onOpenChange={(open) => { if (!open) { setShowVersionModal(false); setPendingStatus(null); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm" data-testid="version-select-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">Choose Final Estimate Version</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-zinc-400 text-sm font-manrope">
              Select the version to use as the final estimate. This will be locked and used for Actual vs Planned comparison.
            </p>
            <div className="flex flex-col gap-2">
              {(project.versions || []).map(v => {
                const vTotal = (v.resources || []).reduce((sum, res) => sum + (res.weeklyAllocation || []).reduce((s, w) => s + (w || 0), 0) * (res.rate || 0), 0);
                return (
                  <Button
                    key={v.version}
                    variant="outline"
                    onClick={() => finalizeVersion(v.version)}
                    data-testid={`finalize-version-${v.version}`}
                    className="justify-between bg-zinc-900 border-zinc-800 hover:border-brand-500 hover:bg-zinc-800 text-left h-auto py-3 px-4"
                  >
                    <span className="font-mono text-brand-400 text-sm">V{v.version}</span>
                    <span className="font-mono text-zinc-300 text-sm">{formatCurrency(vTotal)}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resource Capacity Table */}
      <Card className="bg-zinc-950 border-zinc-900" data-testid="resource-table-card">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="font-manrope font-bold text-base text-white">Resource Capacity & Rates</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={addResource}
            data-testid="add-resource-btn"
            disabled={isLocked}
            className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Resource
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {numWeeks > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="sticky left-0 z-10 bg-zinc-950 min-w-[180px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">
                      Resource
                    </TableHead>
                    <TableHead className="min-w-[120px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">
                      Rate ($/wk)
                    </TableHead>
                    <TableHead className="min-w-[80px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">
                      Apply All
                    </TableHead>
                    {weekLabels.map((label, i) => (
                      <TableHead key={i} className="min-w-[90px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">
                        {label}
                      </TableHead>
                    ))}
                    <TableHead className="min-w-[120px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">
                      Total
                    </TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {project.resources.map((res, resIdx) => (
                    <TableRow key={resIdx} className="border-zinc-900 hover:bg-zinc-900/30" data-testid={`resource-row-${resIdx}`}>
                      <TableCell className="sticky left-0 z-10 bg-zinc-950 p-2">
                        <Select value={res.type} onValueChange={(val) => changeResourceType(resIdx, val)} disabled={isLocked}>
                          <SelectTrigger
                            data-testid={`resource-type-select-${resIdx}`}
                            className="bg-zinc-950 border-zinc-800 text-xs h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-800">
                            {RESOURCE_TYPES.map(r => (
                              <SelectItem key={r.value} value={r.value} className="text-zinc-200 text-xs focus:bg-zinc-900 focus:text-white">
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="p-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-mono">$</span>
                          <Input
                            type="number"
                            data-testid={`resource-rate-${resIdx}`}
                            value={res.rate}
                            onChange={e => updateResource(resIdx, 'rate', Number(e.target.value) || 0)}
                            disabled={isLocked}
                            className="bg-zinc-950 border-zinc-800 font-mono text-xs h-8 pl-5 w-24 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="p-2 text-center">
                        <div className="flex items-center justify-center" title="Copy Week 1 to all weeks">
                          <Checkbox
                            data-testid={`apply-all-checkbox-${resIdx}`}
                            checked={!!applyAllWeeks[resIdx]}
                            onCheckedChange={(checked) => toggleApplyAll(resIdx, checked)}
                            disabled={isLocked}
                            className="border-zinc-600 data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500"
                          />
                        </div>
                      </TableCell>
                      {(res.weeklyAllocation || []).map((val, weekIdx) => (
                        <TableCell key={weekIdx} className="p-1 text-center">
                          <Input
                            type="number"
                            min="0"
                            data-testid={`allocation-${resIdx}-${weekIdx}`}
                            value={val || ''}
                            onChange={e => updateAllocation(resIdx, weekIdx, e.target.value)}
                            placeholder="0"
                            disabled={isLocked}
                            className="bg-zinc-950 border-zinc-800 font-mono text-xs h-8 w-16 text-center mx-auto focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 placeholder:text-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      ))}
                      <TableCell className="p-2 text-right">
                        <span className="font-mono text-sm text-brand-400" data-testid={`resource-total-${resIdx}`}>
                          {formatCurrency(getResourceTotal(res))}
                        </span>
                      </TableCell>
                      <TableCell className="p-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeResource(resIdx)}
                          data-testid={`remove-resource-${resIdx}`}
                          className="text-zinc-600 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                          disabled={project.resources.length <= 1 || isLocked}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter className="bg-zinc-950 border-t border-zinc-800">
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="sticky left-0 z-10 bg-zinc-950 font-manrope font-semibold text-xs uppercase tracking-widest text-zinc-400">
                      Weekly Total
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    {Array.from({ length: numWeeks }).map((_, weekIdx) => (
                      <TableCell key={weekIdx} className="text-center">
                        <span className="font-mono text-xs text-zinc-300" data-testid={`week-total-${weekIdx}`}>
                          {formatCurrency(getWeekTotal(weekIdx))}
                        </span>
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <span className="font-mono text-sm font-semibold text-brand-400" data-testid="grand-total-footer">
                        {formatCurrency(grandTotal)}
                      </span>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          ) : (
            <div className="p-8 text-center text-zinc-500 text-sm" data-testid="no-dates-message">
              <CalendarIcon className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <p>Select start and end dates to configure resource capacity by week</p>
            </div>
          )}
        </CardContent>
        {numWeeks > 0 && (
          <div className="flex justify-end p-4 border-t border-zinc-900">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExportExcel}
              data-testid="export-estimate-btn"
              className="text-zinc-400 hover:text-white hover:bg-zinc-900 font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        )}
      </Card>
        </>
      )}
    </div>
  );
}
